import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export function formatDate(date: string | Date): string {
  return new Date(date).toLocaleString();
}

export function formatCurrency(
  amount: number,
  currency: string = "USD"
): string {
  return `${currency} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "healthy":
    case "ok":
    case "completed":
      return "text-green-500";
    case "degraded":
    case "running":
    case "pending":
      return "text-yellow-500";
    case "unhealthy":
    case "critical":
    case "failed":
      return "text-red-500";
    default:
      return "text-gray-500";
  }
}

export function getStatusBgColor(status: string): string {
  switch (status.toLowerCase()) {
    case "healthy":
    case "ok":
    case "completed":
      return "bg-green-500/10 text-green-500 border-green-500/20";
    case "degraded":
    case "running":
    case "pending":
      return "bg-yellow-500/10 text-yellow-500 border-yellow-500/20";
    case "unhealthy":
    case "critical":
    case "failed":
      return "bg-red-500/10 text-red-500 border-red-500/20";
    default:
      return "bg-gray-500/10 text-gray-500 border-gray-500/20";
  }
}

export function replaceTemplateVariables(
  template: string,
  data: Record<string, unknown>
): string {
  if (!template) return "";
  return template.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_match, key: string) => {
    const value = key
      .split(".")
      .reduce<unknown>(
        (acc, part) =>
          acc == null ? acc : (acc as Record<string, unknown>)[part],
        data
      );
    return value === undefined || value === null ? "" : String(value);
  });
}

export function maskEmail(email: string): string {
  if (!email || typeof email !== "string") return "";
  const [local, domain] = email.split("@");
  if (!domain) return email;
  const visible = local.slice(0, Math.min(2, local.length));
  const masked = "*".repeat(Math.max(local.length - visible.length, 1));
  return `${visible}${masked}@${domain}`;
}

export { type ClassValue } from "clsx";

export {
  type LogLevel,
  type LogMeta,
  type Logger,
  type LoggerFactory,
  createConsoleLogger,
  setLoggerFactory,
  getLogger,
} from "./logger";

export {
  isValidEmail,
  isValidPhone,
  isNonEmpty,
  isPositiveNumber,
  isValidDate,
  isValidContainerNumber,
  isValidBLNumber,
  sanitizeString,
  clampNumber,
} from "./validators";
